import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { pushNotificationsApi } from "@services/api/pushNotifications";
import { showNotification } from "context/notification.service";
import type { User } from "types";

interface SendPushModalProps {
  user: User;
  onClose: () => void;
}

export default function SendPushModal({ user, onClose }: SendPushModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [sending, onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    try {
      const res = await pushNotificationsApi.send({
        title: title.trim(),
        body: body.trim(),
        user_ids: [user.id],
      });
      if (res.success_count > 0) {
        showNotification({ type: "success", message: `Notification envoyée (${res.success_count} appareil${res.success_count > 1 ? "s" : ""})` });
      } else {
        showNotification({ type: "error", message: "Aucun appareil trouvé pour cet utilisateur" });
      }
      onClose();
    } catch (e: any) {
      console.error("Push send error:", e);
      showNotification({ type: "error", message: "Impossible d'envoyer la notification" });
    } finally {
      setSending(false);
    }
  };

  const userName = user.infos.full_name || user.infos.display_name || user.infos.email;

  const modalContent = (
    <div
      className="fixed inset-0 z-50 bg-shadow_50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="bg-white border-1 border-neutral-300 rounded-2xl w-full max-w-md flex flex-col"
      >
        <div className="flex flex-col gap-2 p-6 pb-4">
          <div className="text-h2 font-semibold text-black">
            Envoyer une notification push
          </div>
          <div className="text-h4 text-neutral-400">
            À {userName}
          </div>
        </div>

        <div className="flex flex-col gap-3 px-6 pb-4">
          <input
            type="text"
            placeholder="Titre"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-h3 outline-none focus:border-neutral-500"
            autoFocus
          />
          <textarea
            placeholder="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-h3 outline-none focus:border-neutral-500 resize-none"
          />
        </div>

        <div className="flex flex-row gap-3 p-6 pt-4 justify-end border-t-1 border-neutral-200">
          <SecondaryButton
            label="Annuler"
            onClick={onClose}
            variant="small"
            disabled={sending}
          />
          <SecondaryButton
            label={sending ? "Envoi..." : "Envoyer"}
            onClick={handleSend}
            variant="small"
            disabled={sending || !title.trim() || !body.trim()}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
